/**
 * SpotlightRoi — snapshot dashboard of highlighted-job performance.
 *
 * Rows: per job — active flag, days-left, views, applies, conversion %,
 * a "recommend extending" chip when the job is running out of spotlight
 * but still driving healthy applies. Clicking a row opens the job.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, TrendingUp, Clock, ArrowRight } from "lucide-react";

export default function SpotlightRoi() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/spotlight-roi");
                setData(data);
            } catch (err) { toast.error(formatApiError(err)); }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
    if (!data) return null;

    const s = data.summary || {};
    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="spotlight-roi-page">
            <div className="flex items-center gap-3 flex-wrap">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <h1 className="font-display font-bold text-2xl flex-1">Spotlight ROI</h1>
                <div className="flex gap-2 text-xs">
                    <Stat label="Total tracked" value={s.total_jobs} />
                    <Stat label="Currently lit" value={s.currently_lit} tone="emerald" />
                    <Stat label="Expiring ≤ 3d" value={s.expiring_soon} tone="amber" />
                </div>
            </div>

            {data.rows.length === 0 ? (
                <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                    No highlighted jobs yet. Promote a job from any job detail page or the /jobs list.
                </div>
            ) : (
                <div className="border border-border bg-white">
                    <div className="grid grid-cols-[1fr_100px_90px_90px_90px_140px] gap-2 px-3 py-2 border-b border-border bg-secondary/40 text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                        <span>Job</span><span>Status</span><span className="text-right">Views</span><span className="text-right">Applies</span><span className="text-right">Conv%</span><span></span>
                    </div>
                    {data.rows.map((r) => (
                        <button
                            key={r.id}
                            type="button"
                            onClick={() => navigate(`/jobs/${r.id}`)}
                            className="w-full grid grid-cols-[1fr_100px_90px_90px_90px_140px] gap-2 px-3 py-2 border-t border-border hover:bg-secondary/30 items-center text-left"
                            data-testid={`spot-roi-row-${r.id}`}
                        >
                            <div className="min-w-0 flex items-center gap-2">
                                <span className="w-2 h-6 shrink-0" style={{ background: r.highlight_color }} />
                                <span className="text-sm truncate">{r.title}</span>
                            </div>
                            <span className="text-[10px] font-mono uppercase tracking-widest">
                                {r.active ? (
                                    <span className="text-emerald-800">
                                        <Clock className="inline w-3 h-3 mr-0.5" />
                                        {/* Very-far-future dates (e.g. 2099) render an unfriendly
                                         *  4-digit day count. Cap the display so users see a
                                         *  human-scale label; the underlying value is preserved. */}
                                        {r.days_left > 365 ? "no expiry" : `${r.days_left}d left`}
                                    </span>
                                ) : <span className="text-muted-foreground">Expired</span>}
                            </span>
                            <span className="tabular-nums text-right text-sm">{r.views}</span>
                            <span className="tabular-nums text-right text-sm">{r.applies}</span>
                            <span className="tabular-nums text-right text-sm">{r.conversion_pct}%</span>
                            <span className="text-right">
                                {r.recommend_extend ? (
                                    <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-400 text-amber-900 bg-amber-50 inline-flex items-center gap-1" data-testid={`spot-roi-extend-${r.id}`}>
                                        <TrendingUp className="w-3 h-3" /> extend
                                    </span>
                                ) : (
                                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground ml-auto" />
                                )}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, tone }) {
    const cls = tone === "emerald" ? "border-emerald-300 text-emerald-800 bg-emerald-50"
        : tone === "amber" ? "border-amber-300 text-amber-800 bg-amber-50"
        : "border-border text-foreground bg-white";
    return (
        <div className={`px-2 py-1 border ${cls}`}>
            <div className="text-[9px] font-mono uppercase tracking-widest">{label}</div>
            <div className="tabular-nums font-semibold text-sm">{value ?? 0}</div>
        </div>
    );
}
